import './App.css'
import type {JSX, ReactElement, ReactNode} from 'react'

import {Provider} from '@rollbar/react'
import {BrowserRouter as Router, Navigate, Route, Routes} from 'react-router'

import Admin from './components/Admin'
import {AppHeader} from "./components/common/AppHeader";
import {Notifications} from "./components/common/Notifications";
import Intro from './components/Intro'
import Login from './components/Login'
import Wizard from './components/Wizard'
import {RollbarSettings} from './constants/rollbar.env'
import {useAppSelector} from './hooks'
import type {RootState} from './store'

const rollbarConfig = {
  accessToken: RollbarSettings.AccessToken,
  environment: RollbarSettings.Environment
}

/**
 * App routes.
 */
export const AppRoutes = {
  home: '/',
  login: '/login',
  admin: '/admin',
  wizard: '/wizard'
}

function App(): ReactElement {
  return (
    <Provider config={rollbarConfig}>
      <Router>
        <AppHeader/>
        <Notifications/>
        <div className='container mb-4'>
          <Routes>
            <Route
              path={AppRoutes.home} index element={
              <UnauthorizedRedirect to={AppRoutes.login}>
                <Intro/>
              </UnauthorizedRedirect>
            }
            />
            <Route path={AppRoutes.login} element={<Login/>}/>
            <Route
              path={AppRoutes.wizard} element={
              <UnauthorizedRedirect to={AppRoutes.login}>
                <Wizard/>
              </UnauthorizedRedirect>
            }
            />
            <Route
              path={AppRoutes.admin} element={
              <NonAdminRedirect to={AppRoutes.home}>
                <Admin/>
              </NonAdminRedirect>
            }
            />
          </Routes>
        </div>
      </Router>
    </Provider>
  )
}

export default App

/* Helper components */

export function LoadingSpinner(): JSX.Element {
  return (
    <div className='container'>
      <div className='row'>
        <div className='col-12 text-center'>
          <div className='loading-spinner' data-testid='loading-spinner'/>
        </div>
      </div>
    </div>
  )
}

interface RedirectComponentProps {
  to: string,
  children: ReactNode
}

export function UnauthorizedRedirect({to, children}: Readonly<RedirectComponentProps>): ReactElement {
  const authPending = useAppSelector<boolean>((state: RootState) => state.auth.pending)
  const loggedOut = useAppSelector((state: RootState) => !state.auth.token)
  const claimsPending = useAppSelector<boolean>((state: RootState) => state.user.claimsPending)
  const isAuthorized = useAppSelector<boolean>((state: RootState) =>
    state.user.isAuthenticated && (state.user.claims?.isAuthorized ?? false))

  if (authPending || claimsPending) {
    return <LoadingSpinner/>
  }

  if (!isAuthorized || loggedOut) {
    return <Navigate to={to}/>
  }

  return <>{children}</>
}

export function NonAdminRedirect({to, children}: Readonly<RedirectComponentProps>): ReactElement {
  const claimsPending = useAppSelector<boolean>(state => state.user.claimsPending)
  const isAdmin = useAppSelector<boolean>(state =>
    state.user.isAuthenticated && (state.user.claims?.isAdmin ?? false))

  if (claimsPending) {
    return <LoadingSpinner/>
  }

  if (!isAdmin) {
    return <Navigate to={to}/>
  }

  return <>{children}</>
}
